/**
 * The pad's right-hand column: a search field, the source tabs, and the rows.
 *
 * Lifted out of `Dialer.tsx` when the business register became a third source.
 * Its own file because this half answers a different question from the keypad
 * beside it - "who am I calling" rather than "what number" - and because three
 * sources with three empty states is where a single component stops being one
 * thing (AGENTS.md section 4).
 *
 * It draws and reports; every fetch and every filter lives in `useDialLists`.
 */
import { Phone, Search } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { normalizeE164 } from "../../../lib/phone";
import { Avatar, PillTabs } from "../../ui";
// The leaf and NOT the section's barrel, which is the one place in this repo
// that rule is worth breaking. That barrel re-exports the whole brain card -
// the dialog, the e-mail composer, the call script, the history tab - and the
// call window it would land in is mounted on every page of the CRM. A 79-line
// badge is not worth carrying 2300 lines of card into the shell's chunk.
import { BrainBadgeCell } from "../../sections/ariregister/brain/BrainBadgeCell";
import { REGISTER_MIN_QUERY, type DialLists, type DialTab } from "./useDialLists";
import type { Candidate } from "./candidate";

export function DialPicks({
  lists,
  canSearchRegister,
  onPick,
  onCall,
}: {
  lists: DialLists;
  /**
   * This operator may read the business register.
   *
   * The tab is not merely disabled without it - it is absent. A tab that opens
   * onto a permission error is worse than no tab, and the register is a
   * platform-staff surface that most of this CRM's users never see.
   */
  canSearchRegister: boolean;
  /** The row was chosen: put its number in the field, do not dial it. */
  onPick: (candidate: Candidate) => void;
  /** The green disc was pressed: dial this one now. */
  onCall: (candidate: Candidate) => void;
}) {
  const t = useT();
  const { tab, setTab, find, setFind, rows, busy, needsQuery } = lists;

  const tabs: { value: DialTab; label: string }[] = [
    { value: "recent", label: t("calls.dial.recent") },
    { value: "contacts", label: t("calls.dial.contacts") },
  ];
  if (canSearchRegister) tabs.push({ value: "register", label: t("calls.dial.register") });

  return (
    /* Two elements for one column, and the outer one is empty on purpose.
       This column sits in the same grid row as the keypad, so whichever of the
       two is taller decides how tall the dial pad is - and a list of everybody
       recently called is arbitrarily tall. Left to itself it either dragged the
       window down to the height of its own rows or, once the window was pinned
       to stop that, left the keypad column holding 77px of slack it had no use
       for (see `.crm-modal` in index.css).
       Absolutely positioned, the inner column contributes NOTHING to the row's
       height and then fills whatever the keypad settled on. That is the whole
       trick: the keys decide the height, the list obeys it, and the rows that
       do not fit are behind the scroll they were always behind. */
    <div className="relative hidden border-l border-[var(--crm-hair)] md:block">
      <div className="absolute inset-0 flex min-h-0 flex-col p-5">
        <label className="flex h-11 flex-none items-center gap-2.5 rounded-full bg-[var(--crm-sunk)] px-4">
          <Search className="size-4 text-muted-foreground" />
          <input
            value={find}
            onChange={(e) => setFind(e.target.value)}
            placeholder={t(tab === "register" ? "calls.dial.findRegister" : "calls.dial.findPlaceholder")}
            className="min-w-0 flex-1 border-0 bg-transparent text-[13.25px] font-[550] outline-none"
            autoComplete="off"
            spellCheck={false}
          />
        </label>

        <PillTabs small className="my-3.5" value={tab} onChange={setTab} tabs={tabs} />

        {/* No height of its own: it takes what the column leaves it, and the
            column is exactly as tall as the keypad next door.
            It used to carry a 322px cap, which was one guess at how tall that
            keypad happens to be - and being a guess it was WRONG in the only
            direction that shows: the list stopped roughly forty pixels above
            the column's own bottom edge, so a source with more rows than fit
            was scrolling while a strip of empty card sat under it. Nobody
            guesses now; the wrapper above makes the two columns agree. */}
        {/* Centred while it is empty, top-aligned once there are rows. The three
            silences below are a sentence in the middle of a column, not a first
            row - and in a column this tall a hint pinned to the top of 400px of
            nothing reads as a list that failed to load. */}
        <div
          className={cn(
            "-mx-2 min-h-0 flex-1 overflow-y-auto px-2 pb-0.5",
            rows.length === 0 && "flex items-center justify-center",
          )}
        >
          {rows.length === 0 ? (
            <p className="px-4 pb-6 text-center text-[12.75px] text-muted-foreground">
              {/* Three different silences, and they are not interchangeable.
                  "Type more" is an instruction, "looking" is a wait, and
                  "nothing matched" is an answer - a register tab that said the
                  last one while it had not searched yet would be lying about
                  376 000 companies. */}
              {needsQuery
                ? t("calls.dial.registerHint", { n: REGISTER_MIN_QUERY })
                : busy
                  ? t("calls.dial.searching")
                  : tab === "register"
                    ? t("calls.dial.noRegisterMatch")
                    : t("calls.dial.noMatch")}
            </p>
          ) : (
            rows.map((row) => (
              <button key={row.key} type="button" className="crm-dpick" onClick={() => onPick(row)}>
                <Avatar name={row.name} size={34} />
                <span className="min-w-0 flex-1 text-left">
                  <span className="block text-[13px] font-[650] tracking-[-0.02em]">{row.name}</span>
                  <span className="crm-trunc mt-[3px] block text-[11.5px] tabular-nums text-muted-foreground">
                    {row.detail}
                  </span>
                </span>
                {/* The verdict, at the one moment it can still change what
                    happens: before the disc is pressed. The same glyph the lead
                    list and the register list draw, deliberately - an operator
                    who has learned the colour there must not have to learn a
                    second vocabulary here. Empty for the companies nobody has
                    run, which is most of them. */}
                {row.brain ? (
                  <span className="flex-none">
                    <BrainBadgeCell brain={row.brain} t={t} />
                  </span>
                ) : null}
                {row.meta ? <span className="flex-none text-[11px] text-muted-foreground">{row.meta}</span> : null}
                <span
                  className="crm-dpick-go"
                  onClick={(e) => {
                    // The green disc is the call itself; the row only picks it.
                    e.stopPropagation();
                    if (normalizeE164(row.number)) onCall(row);
                  }}
                >
                  <Phone className="size-4" />
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
